import { bootstrapApplication } from '@angular/platform-browser';
import { appConfig } from './app/app.config';
import { App } from './app/app';
import { TenantInitError, TenantInitService } from './app/core/services/tenant-init.service';
import { ErrorCodesService } from './app/core/services/error-codes.service';

/**
 * Apply SEO meta tags from tenant config before Angular bootstrap.
 * This ensures crawlers see tenant-specific meta immediately.
 */
function applyPreBootstrapSeo(tenantConfig: any): void {
  if (typeof document === 'undefined' || !tenantConfig) return;

  const branding = tenantConfig.branding;
  const brandName = branding?.name || tenantConfig.tenantName || 'School ERP';
  const description = branding?.description ||
    'Smart school management platform for admissions, attendance, communication, and daily reports.';
  const logo = branding?.logo || '';
  const primaryColor =
    tenantConfig.theme?.mainColors?.primaryColor ||
    branding?.primaryColor ||
    '#1A237E';
  const url = window.location.origin;
  const pageTitle = `${brandName} - Smart School Management`;

  document.title = pageTitle;

  const setMeta = (attr: string, key: string, content: string) => {
    const el = document.querySelector(`meta[${attr}="${key}"]`) as HTMLMetaElement | null;
    if (el) el.setAttribute('content', content);
  };

  setMeta('name', 'description', description);
  setMeta('name', 'author', brandName);
  setMeta('name', 'theme-color', primaryColor);

  setMeta('property', 'og:title', pageTitle);
  setMeta('property', 'og:description', description);
  setMeta('property', 'og:site_name', brandName);
  setMeta('property', 'og:url', url);
  if (logo) setMeta('property', 'og:image', logo);

  setMeta('name', 'twitter:title', pageTitle);
  setMeta('name', 'twitter:description', description);
  if (logo) setMeta('name', 'twitter:image', logo);

  const canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
  if (canonical) canonical.setAttribute('href', url);
}

function redirectToErrorPage(errorType: string, tenantId?: string): void {
  if (typeof window === 'undefined') {
    return;
  }

  const params = new URLSearchParams();
  params.set('code', ErrorCodesService.getErrorTokenByType(errorType));
  if (tenantId) {
    params.set('tenantId', tenantId);
  }

  window.location.href = `/error?${params.toString()}`;
}

function redirectToTenantSelect(): void {
  if (typeof window === 'undefined') {
    return;
  }

  window.location.href = '/tenant';
}

async function initializeApp() {
  if (typeof window === 'undefined') {
    await bootstrapApplication(App, appConfig);
    return;
  }

  if (window.location.pathname === '/error' || window.location.pathname === '/tenant') {
    await bootstrapApplication(App, appConfig);
    return;
  }

  const tenantInitService = new TenantInitService();
  const tenantId = tenantInitService.extractTenantId();
  if (!tenantId) {
    redirectToTenantSelect();
    return;
  }

  try {
    const tenantConfig = await tenantInitService.initializeTenant(tenantId);
    (window as any).__tenantConfig = tenantConfig;

    // Apply SEO meta tags from tenant config before Angular bootstrap
    applyPreBootstrapSeo(tenantConfig);

    // Bootstrap Angular - let the router and auth guards handle navigation
    // No need to manually redirect - Angular router will handle:
    // - '' -> '/dashboard' (via routes)
    // - Auth guard will redirect to '/auth/login' if not authenticated
    await bootstrapApplication(App, appConfig);
  } catch (error: any) {
    if (error instanceof TenantInitError) {
      redirectToErrorPage(error.code, error.tenantId || tenantId);
      return;
    }

    redirectToErrorPage('TENANT_API_ERROR', tenantId);
  }
}

initializeApp();
